/**
 * Tiny utilities shared across the design system.
 *
 * `cn` merges class names the shadcn way: `clsx` resolves conditionals and
 * `tailwind-merge` de-dupes conflicting Tailwind utilities (last wins). Plain
 * `.it-*` classes pass straight through, so legacy callers are unaffected.
 *
 * `rng` is a deterministic seeded PRNG — keeps charts stable across renders
 * (we want the same dummy data each mount, not real randomness).
 *
 * `smoothPath` turns a list of `[x, y]` points into a quadratic-Bezier SVG `d`
 * attribute. Used by sparklines and any small inline trend chart.
 */
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

export type Rng = () => number

export function rng(seed: number): Rng {
  let s = seed
  return () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
}

export type Point = readonly [number, number]

export function smoothPath(pts: readonly Point[]): string {
  if (pts.length < 2) return ''
  let d = `M ${pts[0][0]},${pts[0][1]}`
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]
    const [x1, y1] = pts[i + 1]
    const mx = (x0 + x1) / 2
    d += ` Q ${x0},${y0} ${mx},${(y0 + y1) / 2} T ${x1},${y1}`
  }
  return d
}
